import React, { useState, useEffect } from 'react';

const hints = [
    {
      category: "Getting Started",
      title: "Results Follow the Inputs",
      description: "Every figure comes from the numbers on the form. Recent local sales and listings will give you better prices and after-repair values than national averages.",
    },
    {
      category: "Getting Started",
      title: "Property Condition",
      description: "The condition you select sets the renovation estimate and how many months the work takes. 'Fair' assumes cosmetic work; 'Poor' assumes major repairs and can more than double the cost.",
    },
    {
      category: "Strategy Selection",
      title: "Location and Strategy",
      description: "Nightly rates are estimated by state, so short-term rental figures vary most with location. Enter your own nightly rate and rent when you have local figures.",
    },
    {
      category: "Strategy Selection",
      title: "Workload Differs by Strategy",
      description: "The workload score is fixed. A short-term rental counts as the most hands-on, a flip as intense but short, and a long-term rental as the least effort.",
    },
    {
      category: "Financial Analysis",
      title: "Reading Cash Flow",
      description: "Cash flow is rent after all costs and loan payments. A negative figure means the owner pays the difference each month. Some investors use a rule of thumb of $100-200 a month per unit as a cushion.",
    },
    {
      category: "Financial Analysis",
      title: "The 1% Rule",
      description: "A common rule of thumb compares monthly rent with 1% of the purchase price as a quick screen. Few properties meet it in high-priced markets, and it ignores taxes, insurance and financing.",
    },
    {
      category: "Financial Analysis",
      title: "Total ROI vs Cash-on-Cash",
      description: "Cash-on-cash counts only the yearly cash flow. Total ROI also counts the change in value and the loan paid down over five years, so it depends on the appreciation rate you enter.",
    },
    {
      category: "Risk Management",
      title: "Sensitivity Sliders",
      description: "The sliders change purchase price, renovation cost, after-repair value, rent, nightly rate and occupancy, and every figure updates. They show what a 20% lower rent or 50% occupancy does to each strategy.",
    },
    {
      category: "Risk Management",
      title: "What the Deal Grade Means",
      description: "The grade (Excellent, Good, Fair, Poor) places the flip figures against fixed thresholds for ROI, after-repair value and renovation cost. It is a label for the numbers, not a verdict on the property.",
    },
    {
      category: "Risk Management",
      title: "Reserves Are Not Modelled",
      description: "The analysis doesn't hold any cash back. A common rule of thumb is six months of expenses for a rental and 10-20% extra on a renovation budget. The renovation slider shows what an overrun does.",
    },
    {
      category: "Fix & Flip",
      title: "The 70% Rule",
      description: "A common rule of thumb among flippers is a purchase price of at most 70% of after-repair value minus renovation costs. Example: $200k ARV x 70% - $30k renovation = $110k. The results show this figure for your inputs.",
    },
    {
      category: "Fix & Flip",
      title: "Holding Time and Profit",
      description: "Each month held adds interest and holding expenses, and lowers the annualized return. The renovation timeline is an estimate from the property's condition, not a schedule for your project.",
    },
    {
      category: "Long-Term Rental",
      title: "Management Fee",
      description: "The long-term rental figures include a property management fee (8% of collected rent unless you change it), whether or not you plan to manage the property yourself.",
    },
    {
      category: "Long-Term Rental",
      title: "Vacancy Allowance",
      description: "The form starts at 5% vacancy, about 18 days a year. Rates vary by market and many investors use 5-10%. Change the figure on the form to see the effect on cash flow.",
    },
    {
      category: "Short-Term Rental",
      title: "Local Rules Are Not Modelled",
      description: "Many cities, and some homeowner associations, restrict or ban short-term rentals or require permits. The analysis assumes the property can be rented nightly.",
    },
    {
      category: "Short-Term Rental",
      title: "Short-Term Rental Costs",
      description: "The figures include cleaning per stay, supplies, higher utilities and insurance, upkeep and a 20% management fee. Together these often take around half of revenue.",
    },
    {
      category: "Short-Term Rental",
      title: "Seasonality",
      description: "The analysis uses one occupancy rate for the whole year. Beach towns peak in summer and ski towns in winter, so enter a yearly average. The occupancy slider shows a weaker year.",
    },
    {
      category: "Priority Scoring",
      title: "Your Priorities Set the Weights",
      description: "The weight sliders decide how much return, cash flow, risk and workload count toward each strategy's score. Changing them can change which strategy scores highest.",
    },
    {
      category: "Priority Scoring",
      title: "Scores Involve Trade-Offs",
      description: "A higher return usually comes with a worse risk or workload score. The top-scoring strategy reflects your weights and numbers. It's a model result, not advice.",
    },
    {
      category: "Charts & Data",
      title: "The 5-Year Projection",
      description: "Rent rises 2.5% a year and costs rise with them. Year 1 is lower when part of it is spent renovating, because no rent is counted for those months.",
    },
    {
      category: "Charts & Data",
      title: "The Same Five Years",
      description: "A flip frees its cash in months and a rental holds it for years. This panel starts each strategy with the same cash and shows what you would have after five years, plus the appreciation at which holding ends level with flipping.",
    },
    {
      category: "Advanced Notes",
      title: "Market Cycles",
      description: "The analysis uses today's price and one steady appreciation rate. Prices and rents move in cycles, so try a lower or negative appreciation rate to see how much of the result depends on it.",
    },
    {
      category: "Advanced Notes",
      title: "Reinvestment Rate",
      description: "The same-five-years comparison assumes cash outside the property, including flip proceeds, earns the rate you set. A higher rate favors flipping, and a lower one favors holding.",
    },
    {
      category: "Advanced Notes",
      title: "Flip Financing",
      description: "A flip can be analyzed with the long-term loan, a hard-money loan with points and interest-only payments, or cash. The choice changes cash to close, holding costs and ROI.",
    },
    {
      category: "Common Pitfalls",
      title: "Optimistic After-Repair Value",
      description: "Flip profit is most sensitive to the after-repair value. Moving the ARV slider down 10-15% shows how thin the margin is.",
    },
    {
      category: "Common Pitfalls",
      title: "Unverified Rents",
      description: "Online rent estimates can differ from what local listings and property managers report. The rent slider shows the effect of a lower figure.",
    },
    {
      category: "Common Pitfalls",
      title: "Costs Outside the Model",
      description: "Closing and selling costs are included. Permits, inspection surprises and income tax on the profit aren't, unless you add them to the renovation figure. HOA dues, when entered, count for every strategy.",
    }
];

const CATEGORY_COLORS = {
  "Getting Started": "text-terminal-green",
  "Strategy Selection": "text-terminal-cyan",
  "Financial Analysis": "text-terminal-amber",
  "Risk Management": "text-terminal-red",
  "Fix & Flip": "text-terminal-cyan",
  "Long-Term Rental": "text-terminal-green",
  "Short-Term Rental": "text-terminal-amber",
  "Priority Scoring": "text-terminal-cyan",
  "Charts & Data": "text-terminal-green",
  "Advanced Notes": "text-terminal-amber",
  "Common Pitfalls": "text-terminal-red",
};

const DISMISSED_KEY = 'ultronic_tips_dismissed';

const TutorialHints = () => {
  const [currentHint, setCurrentHint] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [showAllHints, setShowAllHints] = useState(false);

  useEffect(() => {
    let dismissed = '1';
    try {
      dismissed = localStorage.getItem(DISMISSED_KEY);
    } catch {
      // Storage unavailable: skip the automatic tip.
    }
    // On a phone the tip would cover the results, so it waits to be asked for.
    if (!dismissed && window.innerWidth >= 640) {
      const timer = setTimeout(() => {
        const randomHint = hints[Math.floor(Math.random() * hints.length)];
        setCurrentHint(randomHint);
        setShowHint(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const getRandomHint = () => {
    const randomHint = hints[Math.floor(Math.random() * hints.length)];
    setCurrentHint(randomHint);
    setShowHint(true);
  };

  const dismissHint = () => {
    setShowHint(false);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Storage unavailable: the tip may show again next time.
    }
    setTimeout(() => setCurrentHint(null), 300);
  };

  const hintsByCategory = hints.reduce((acc, hint) => {
    if (!acc[hint.category]) acc[hint.category] = [];
    acc[hint.category].push(hint);
    return acc;
  }, {});

  if (showAllHints) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Real estate tips">
        <div className="terminal-card p-0 max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="bg-surface-elevated pl-6 pr-3 py-3 border-b border-surface-border flex items-center justify-between">
            <div>
              <h2 className="font-display font-bold text-terminal-green crt-glow uppercase tracking-wider text-lg">
                Real Estate Tips
              </h2>
              <p className="text-txt-muted font-mono text-xs mt-1">
                {hints.length} tips on reading the results. Rules of thumb are just that, not advice.
              </p>
            </div>
            <button
              onClick={() => setShowAllHints(false)}
              aria-label="Close tips"
              className="min-w-[44px] min-h-[44px] flex-shrink-0 flex items-center justify-center text-txt-muted hover:text-terminal-green transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6 terminal-scrollbar">
            {Object.entries(hintsByCategory).map(([category, categoryHints]) => (
              <div key={category} className="mb-6">
                <h3 className={`font-display font-bold uppercase tracking-wider text-xs mb-3 pb-2 border-b border-surface-border ${CATEGORY_COLORS[category] || 'text-terminal-green'}`}>
                  {category}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {categoryHints.map((hint, index) => (
                    <div key={index} className="bg-surface-elevated rounded-lg p-3 border border-surface-border hover:border-terminal-dark-green transition-colors">
                      <h4 className="font-mono font-medium text-sm text-terminal-cyan mb-1">{hint.title}</h4>
                      <p className="font-mono text-xs text-txt-secondary leading-relaxed">{hint.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-surface-elevated px-6 py-3 border-t border-surface-border flex justify-end">
            <button
              onClick={() => setShowAllHints(false)}
              className="glow-btn glow-btn-green px-4 min-h-[44px] rounded-lg text-xs font-mono uppercase tracking-wider"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {showHint && currentHint && (
        <div className="fixed top-16 right-4 terminal-card p-0 max-w-xs z-40 shadow-glow-green-sm overflow-hidden">
          <div className="bg-surface-elevated pl-3 border-b border-surface-border flex items-center justify-between">
            <span className={`font-display font-bold uppercase tracking-wider text-xs ${CATEGORY_COLORS[currentHint.category] || 'text-terminal-amber'}`}>
              {currentHint.category}
            </span>
            <button
              onClick={dismissHint}
              aria-label="Dismiss tip"
              className="min-w-[44px] min-h-[44px] flex items-center justify-center text-txt-muted hover:text-terminal-green transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-3">
            <h4 className="font-mono font-medium text-sm text-terminal-cyan mb-1">{currentHint.title}</h4>
            <p className="font-mono text-xs text-txt-secondary leading-relaxed">{currentHint.description}</p>
          </div>

          <div className="px-3 pb-2 flex justify-between items-center">
            <button
              onClick={() => setShowAllHints(true)}
              className="min-h-[44px] text-xs font-mono uppercase tracking-wider text-txt-muted hover:text-terminal-green transition-colors"
            >
              View all tips
            </button>
            <button
              onClick={getRandomHint}
              className="min-h-[44px] text-xs font-mono uppercase tracking-wider bg-surface-elevated border border-surface-border hover:border-terminal-dark-green text-txt-secondary hover:text-terminal-cyan px-3 rounded transition-colors"
            >
              Next tip
            </button>
          </div>
        </div>
      )}

      <div className="fixed bottom-6 left-6 z-50">
        <button
          onClick={() => setShowAllHints(true)}
          className="bg-surface-elevated border border-terminal-dark-green hover:border-terminal-green text-terminal-green rounded-full p-3 transition-all duration-200 hover:shadow-glow-green-sm group"
          title="View real estate tips"
          aria-label="View real estate tips"
        >
          <svg className="w-5 h-5 group-hover:drop-shadow-[0_0_6px_var(--terminal-green)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </button>
      </div>
    </>
  );
};

export default TutorialHints;
